import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./WithdrawalList.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const API_BASE_URL = "https://aadyaacademy.org/api";

function WithdrawalList() {
  const [withdrawalList, setWithdrawalList] = useState([]);
  const [filteredWithdrawals, setFilteredWithdrawals] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState(""); 
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return "Invalid Date";
    }
  };

  const fetchWithdrawals = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/withdrawals/list`)
      .then((res) => res.json())
      .then((data) => {
        const rawList = Array.isArray(data) ? data : (data.data || []);
        
        const formatted = rawList.map((item) => ({
          student_name: item.enquiry_name,
          phone: item.father_phone || item.mother_phone || "N/A",
          email: item.father_email || "N/A",
          withdrawal_date: formatDate(item.updated_at || item.withdraw_date),
          academic_year: item.academic_year_joined || "Unknown",
        }));

        setWithdrawalList(formatted);
        setFilteredWithdrawals(formatted);

        const extractedYears = [
          ...new Set(formatted.map((rec) => rec.academic_year)),
        ].sort((a, b) => {
          const y1 = parseInt(a.split("-")[0]) || 0;
          const y2 = parseInt(b.split("-")[0]) || 0;
          return y2 - y1;
        });

        setYears(extractedYears);
        if (extractedYears.length > 0) {
          setSelectedYear(extractedYears[0]); 
        }
        setLoading(false);
      })
      .catch((error) => {
        toast.error("Failed to load withdrawals");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  useEffect(() => {
    let filtered = [...withdrawalList];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (wd) =>
          wd.student_name?.toLowerCase().includes(term) ||
          wd.email?.toLowerCase().includes(term) ||
          wd.phone?.includes(term)
      );
    }
    if (selectedYear) {
      filtered = filtered.filter((wd) => wd.academic_year === selectedYear);
    }
    setFilteredWithdrawals(filtered);
  }, [searchTerm, selectedYear, withdrawalList]);

  const downloadExcel = () => {
    if (filteredWithdrawals.length === 0) {
      toast.info("No data to export");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(filteredWithdrawals);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Withdrawals");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const file = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(file, `Withdrawals_${selectedYear || "All"}.xlsx`);
  };

  return (
    <div className="withdrawals">
      <div className="withdrawals-table">
        <div className="table-header">
          <div className="table-header-top">
            <h2>WITHDRAWAL RECORDS: {filteredWithdrawals.length}</h2>
          </div>
          <div className="table-header-btm">
            {/* Input Section */}
            <input
              type="text"
              className="search-input"
              placeholder="Search Name/Phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            {/* Year Filter Section */}
            <select
              className="status-filter"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="">All Years</option>
              {years.map((yr, index) => (
                <option key={index} value={yr}>{yr}</option>
              ))}
            </select>

            {/* Download Button placed after sections in same row */}
            <button className="download-excel-btn" onClick={downloadExcel}>
              Download Excel
            </button>
          </div>
        </div>

        <div className="table-content">
          <table className="table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Withdrawal Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center">Loading...</td></tr>
              ) : filteredWithdrawals.length === 0 ? (
                <tr><td colSpan="5" className="text-center">No records found</td></tr>
              ) : (
                filteredWithdrawals.map((wd, index) => (
                  <tr key={index} className="withdrawal-row">
                    <td>{index + 1}</td>
                    <td>{wd.student_name}</td>
                    <td>{wd.phone}</td>
                    <td>{wd.email}</td>
                    <td>{wd.withdrawal_date}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default WithdrawalList;